import { useMemo, type ReactNode } from 'react';
import { useQuery } from 'react-query';
import type {
  TripTagOption,
  TripWizardConfig,
  WizardValues,
} from '@koob/ui/src/features/tripWizard/types';
import { clearStayRoomsExceedingTrip } from '@koob/utils';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import {
  seedFromComposition,
  seedFromTripFilters,
  syncDaysWithDuration,
  tripFiltersFromValues,
} from '../logic/designTrip';
import useTripStore from '../store/useTripStore';
import { useSetTripFilters, useTripFilters } from './useTripStoreSelectors';

// A trip designed from a template keeps the template's countries and DMC: its
// itinerary is built on that DMC's products.
const TEMPLATE_FIXED_STEPS = ['countries', 'dmc'];

const STALE = 5 * 60 * 1000;

interface Options {
  mode: 'create' | 'edit';
  // Create: pax and birthdates of the client folder the trip is for.
  seedComposition?: Parameters<typeof seedFromComposition>[0];
  // Create: start from the trip in the store (a template just opened). Edit
  // always starts from it.
  seedFromTrip: boolean;
  onDone?: () => void;
  onExit?: () => void;
}

function useTagOptions(
  key: string,
  locale: string,
  fetch: (locale: string) => Promise<{ data: unknown }>,
): TripTagOption[] {
  const { data } = useQuery(['designTrip', key, locale], () => fetch(locale), {
    staleTime: STALE,
  });
  return useMemo(
    () =>
      (((data as any)?.data ?? []) as any[]).map((x) => ({
        id: x.id,
        label: x.displayName,
      })),
    [data],
  );
}

// What the design-trip wizard needs: its form config (lists, save, what
// happens after the save), the answers to start from, and the ids it reads off
// the trip in the store. What differs between the apps comes from
// `config.designTrip`.
export function useDesignTripForm({
  mode,
  seedComposition,
  seedFromTrip,
  onDone,
  onExit,
}: Options) {
  const { api, designTrip: host, locale: appLocale } = useTripDesignerConfig();
  if (!host) {
    throw new Error('The design-trip wizard needs config.designTrip');
  }
  const tripFilters = useTripFilters();
  const setTripFilters = useSetTripFilters();
  const locale: string =
    tripFilters?.locale ?? tripFilters?.clientFolder?.locale ?? appLocale;

  const themeOptions = useTagOptions('themes', locale, () => api.getThemes());
  const hotelStyleOptions = useTagOptions('styles', locale, (l) =>
    api.getStyles({ locale: l }),
  );

  const config = useMemo<TripWizardConfig>(
    () => ({
      t: host.t,
      locale,
      localeOptions: host.localeOptions,
      briefReadOnly: host.briefReadOnly,
      themeOptions,
      hotelStyleOptions,
      dmc: host.dmc,
      loadCountries: host.loadCountries,
      loadCities: (query) => host.loadCities(query, locale),
      // buildTripPayload already attaches a complete tripFilters, so the whole
      // body is posted as is.
      saveTrip: api.saveTrip as TripWizardConfig['saveTrip'],
      onSaved: (tripId: string, values: WizardValues) => {
        const tripLocale = values.locale ?? locale;
        if (mode === 'edit') {
          const store = useTripStore.getState();
          const days = host.syncDaysWithDuration
            ? syncDaysWithDuration(store.days, values.duration)
            : store.days;
          const nextDays = clearStayRoomsExceedingTrip(days, {
            adults: values.adultPax,
            childrenBirthdates: values.childrenBirthdates,
          });
          if (nextDays !== store.days) store.updateDays(nextDays);
        }
        const saved = tripFiltersFromValues(values, {
          tripId,
          locale: tripLocale,
          dmcId: host.dmc.mode === 'fixed' ? host.dmc.id : values.dmcId,
        });
        setTripFilters((current: any) =>
          host.replacesTripFilters ? saved : { ...current, ...saved },
        );
        onDone?.();
        host.afterSave({ tripId, locale: tripLocale });
      },
      // No exit given: no close button, rather than one that does nothing.
      onExit,
    }),
    // `api.saveTrip` is read at save time: the app's config object is not
    // guaranteed stable, and rebuilding the wizard config resets the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      host,
      locale,
      themeOptions,
      hotelStyleOptions,
      mode,
      onDone,
      onExit,
      setTripFilters,
    ],
  );

  const initialValues = useMemo(() => {
    if (seedFromTrip && tripFilters?.tripId) {
      return seedFromTripFilters(tripFilters);
    }
    if (mode === 'create' && seedComposition) {
      return seedFromComposition(seedComposition);
    }
    return undefined;
  }, [tripFilters, mode, seedFromTrip, seedComposition]);

  return {
    config,
    initialValues,
    tripId: tripFilters?.tripId as string | undefined,
    fixedStepIds: tripFilters?.templateId ? TEMPLATE_FIXED_STEPS : undefined,
    blocked: host.renderBlocked?.(onExit) as ReactNode,
  };
}
